import React from 'react';
import { __, always, ifElse, gt, lt, prop } from 'ramda';
import { Group } from '@vx/group';
import { ParentSize } from '@vx/responsive';
import { Circle } from '@vx/shape';
import { scaleLinear, scaleOrdinal } from '@vx/scale';
import { useIntl } from 'gatsby-plugin-intl';
import { measureNoWrappedText } from '../../../utils/measureText';
import { Header } from '../header';
import { Grid } from '../grid';
import { NoData, isNoData } from '../noData';
import { BUBBLE_DEFAULT } from '../configs/bubble.config';
import { Container, Chart, Svg } from '../charts.styles';
import { CHART_TYPES, DEFAULT_SIZE } from '../charts.constants';
import {
  Axes,
  BubbleLabel,
  BubbleValue,
  Legend,
  LegendLabels,
  LegendItem,
  ScaleBar,
  bubbleValueStyle,
  bubbleLabelStyle,
  BubbleCounterLabel,
} from './bubbleChart.styles';
import {
  EMPTY_TICK_LABEL,
  MIN_VALUE_RATIO,
  MAX_VALUE_RATIO,
  LIMIT_BUBBLE_RATIO,
  MAX_BUBBLE_RATIO,
  DEFAULT_EXTRA_MARGIN,
  LIMIT_BUBBLE_VALUE_WIDTH,
  LIMIT_BUBBLE_LABEL_WIDTH,
  MAX_BUBBLE_LABEL_SCALE,
  TEXT_HEIGHT_OFFSET_RATIO,
  LABELS_OFFSET_RATIO,
  DEFAULT_BUBBLE_LABEL_SCALE,
} from './bubbleChart.constants';
import { renderWhenTrue } from '../../../utils/rendering';

const rescaleBubble = ({ max, chartHeight, bubbleScale }) => {
  const maxBubble = MAX_BUBBLE_RATIO * chartHeight;
  const currentMaxBubbleDiameter = bubbleScale(max) * 2;
  const bubbleSizeRatio = maxBubble / currentMaxBubbleDiameter;

  const extraMargin = ifElse(
    lt(__, LIMIT_BUBBLE_RATIO),
    () => {
      const currentWidth = bubbleScale.range()[1];
      const newWidth = currentWidth * bubbleSizeRatio;
      const newExtraMargin = (currentWidth - newWidth) / 2;

      bubbleScale.range([0, newWidth]);

      return newExtraMargin;
    },
    always(DEFAULT_EXTRA_MARGIN)
  )(bubbleSizeRatio);

  return extraMargin;
};

const getScales = ({ chartWidth, chartHeight, data, serie, axis, config: { margin, colors } }) => {
  const getAxis = prop(axis);
  const getValue = prop(serie);

  const values = data.map(getValue);
  const min = Math.min(...values);
  const max = Math.max(...values);

  const chartMargin = chartWidth * margin;

  const totals = values.reduce(
    (collection, radius) => {
      const sumValue = radius * 2 + collection.sum;

      collection.radiusList.push(radius);
      collection.sumList.push(sumValue);
      collection.sum = sumValue;

      return collection;
    },
    { sumList: [], radiusList: [], sum: 0 }
  );

  const bubbleScale = scaleLinear({
    domain: [0, totals.sum],
    range: [0, chartWidth - chartMargin * 2],
  });

  const extraMargin = rescaleBubble({ max, chartHeight, bubbleScale });
  const xScale = (index) =>
    bubbleScale(totals.sumList[index]) - bubbleScale(totals.radiusList[index]) + chartMargin + extraMargin;

  const groupScale = scaleOrdinal({
    domain: [EMPTY_TICK_LABEL, ...data.map(getAxis), EMPTY_TICK_LABEL],
    range: [0, ...data.map((_, index) => xScale(index)), chartWidth],
  });

  const mid = min + (max - min) / 2;
  const colorScale = scaleLinear({
    domain: [min, mid, max],
    range: colors,
  });

  const valueScale = scaleLinear({
    domain: [min * MIN_VALUE_RATIO, max * MAX_VALUE_RATIO],
    rangeRound: [chartHeight, 0],
    nice: true,
  });

  return {
    bubbleScalesProps: {
      getAxis,
      getValue,
      xScale,
      valueScale,
      bubbleScale,
      colorScale,
      values: [min, max],
    },
    gridScalesProps: {
      yScale: valueScale,
    },
    axesScalesProps: {
      groupScale,
      valueScale,
    },
  };
};

const renderBubbleLabels = ({ radius, label, value, observations, isBlack }, { bubbleValueFormatter }) => {
  const valueText = bubbleValueFormatter(value);

  const { width: valueWidth = 1, height: valueHeight = 1 } = measureNoWrappedText(valueText, bubbleValueStyle) || {};
  const limitValueWidth = LIMIT_BUBBLE_VALUE_WIDTH * radius;
  const xScale = limitValueWidth / valueWidth;
  const yScale = radius / valueHeight;
  const scale = Math.min(xScale, yScale, MAX_BUBBLE_LABEL_SCALE);
  const yValueOffset = valueHeight * TEXT_HEIGHT_OFFSET_RATIO;
  const groupOffset = radius * LABELS_OFFSET_RATIO;

  const { width: labelWidth = 1 } = measureNoWrappedText(label, bubbleLabelStyle) || {};
  const labelLimitWidth = LIMIT_BUBBLE_LABEL_WIDTH * radius;
  const labelScale = ifElse(
    gt(__, labelLimitWidth),
    always(labelLimitWidth / labelWidth),
    always(DEFAULT_BUBBLE_LABEL_SCALE)
  )(labelWidth);

  return (
    <Group top={observations ? groupOffset * 4 : groupOffset}>
      <BubbleLabel scale={labelScale} isBlack={isBlack}>
        {label}
      </BubbleLabel>
      <BubbleValue scale={scale} yValueOffset={observations ? radius * 0.35 : yValueOffset} isBlack={isBlack}>
        {valueText}
      </BubbleValue>
      {renderWhenTrue(
        always(
          <BubbleCounterLabel yValueOffset={radius} scale={labelScale} isBlack={isBlack}>
            {observations}
          </BubbleCounterLabel>
        )
      )(!!observations)}
    </Group>
  );
};

const renderBubble = ({ getAxis, getValue, colorScale, valueScale, xScale, bubbleScale, values }, config) => (
  bubble,
  index
) => {
  const label = getAxis(bubble);
  const value = getValue(bubble);
  const radius = bubbleScale(value);
  const minMaxDifference = (values[1] - values[0]) / 3;
  const colorBreakpointValue = values[0] + minMaxDifference;

  return (
    <Group key={index} transform={`translate(${xScale(index)}, ${valueScale(value)})`}>
      <Circle fill={colorScale(value)} r={radius} />
      {renderBubbleLabels(
        { radius, label, value, observations: bubble.observations, isBlack: value < colorBreakpointValue },
        config
      )}
    </Group>
  );
};

const renderLegend = ({ colors }) => {
  const { formatMessage } = useIntl();
  return (
    <Legend>
      <LegendLabels>
        <LegendItem>{formatMessage({ id: 'Lowest Score' })}</LegendItem>
        <LegendItem>{formatMessage({ id: 'Highest Score' })}</LegendItem>
      </LegendLabels>
      <ScaleBar colors={colors} />
    </Legend>
  );
};

export const BubbleChart = ({
  width = DEFAULT_SIZE,
  height = DEFAULT_SIZE,
  data: chartData = {},
  parser: customParser,
  config: customConfig = {},
  tabs,
}) => {
  if (customParser) customParser(chartData);
  const intl = useIntl();
  chartData['title'] = intl.formatMessage({ id: chartData.title });
  let { title, subtitle, data, axis, serie } = chartData;
  data = data.map((row) => ({ ...row, income: intl.formatMessage({id:row.income}) }));

  if (isNoData(data, serie, axis)) return <NoData width={width} height={height} />;

  const config = {
    ...BUBBLE_DEFAULT,
    ...customConfig,
  };

  const yOffset = config.yAxisVisible ? config.yAxisSpace : 0;
  const xOffset = config.xAxisVisible ? config.xAxisSpace : 0;

  const type = CHART_TYPES.VERTICAL;

  return (
    <Container width={width} height={height}>
      <Header title={title} subtitle={subtitle} xOffset={xOffset} tabs={tabs} config={config}>
        {renderLegend(config)}
      </Header>
      <Chart>
        <ParentSize>
          {({ width: chartAreaWidth, height: chartAreaHeight }) => {
            if (!chartAreaWidth || !chartAreaHeight) return null;

            const chartWidth = chartAreaWidth - xOffset;
            const chartHeight = chartAreaHeight - yOffset;
            const { bubbleScalesProps, axesScalesProps, gridScalesProps } = getScales({
              chartWidth,
              chartHeight,
              type,
              data,
              serie,
              axis,
              config,
            });

            return (
              <Svg>
                <Grid left={xOffset} width={chartWidth} height={chartHeight} config={config} {...gridScalesProps} />
                <Group left={xOffset}>{data.map(renderBubble(bubbleScalesProps, config))}</Group>
                <Axes
                  type={type}
                  chartHeight={chartHeight}
                  chartWidth={chartWidth}
                  config={config}
                  {...axesScalesProps}
                />
              </Svg>
            );
          }}
        </ParentSize>
      </Chart>
    </Container>
  );
};
